// ---------------------------------------------------------------------------
// THE STATUS VOCABULARY. Pure data and pure functions — no React, no DOM, so
// every rule below is assertable under `node --test` rather than by looking at
// a screenshot.
//
// SOMEBODY READS THIS ON A CRACKED PHONE, IN THE RAIN, AT 06:00.
//
// That sentence is the whole design. It is why status is never conveyed by
// colour alone, and why that is enforced STRUCTURALLY here rather than asked
// for in a style guide:
//
//   * A tone is not a colour, it is a MEANING. There are four, plus neutral,
//     and they are the four the brief names. semantic.css binds each to exactly
//     one primitive token and the parity script checks the binding.
//   * Every tone carries a distinct SHAPE. Two tones that shared a glyph would
//     be distinguishable only by colour again, which is the defect wearing a
//     disguise, so `glyphsAreDistinct()` asserts it.
//   * Every chip carries WORDS. `statusChip` refuses an empty label — a status
//     with no text is not a degraded chip, it is the thing the rule forbids, and
//     it should fail where it is written rather than render silently.
//
// NOTHING HERE NAMES A JOB, A CREDENTIAL OR A PAY RULE. These are tones — the
// design system's vocabulary — and they are the same in every vertical. What a
// tone is called in a given screen ("Checked in", "Licence expiring") is that
// slice's own word, passed in as the label, and ultimately configuration.
// ---------------------------------------------------------------------------

export const TONES = ["live", "attention", "failed", "neutral"] as const;
export type Tone = (typeof TONES)[number];

/**
 * What each tone means. Written down next to the data it governs, because the
 * rule lives in three documents and documents go stale.
 */
export const TONE_MEANING: Record<Tone, string> = {
  live: "Live, confirmed, accepted, in progress. Teal.",
  attention: "Needs a human, expiring, unconfirmed, waiting. Amber.",
  failed: "Failed, refused, expired, blocked. Red.",
  neutral: "Not started, draft, not applicable. Muted ink.",
};

/**
 * A 16×16 icon per tone, as SVG path data, drawn with `fill-rule="evenodd"` so
 * each is a solid silhouette with its mark punched out — legible at 14px on a
 * dim screen, and legible in monochrome.
 *
 * The four silhouettes are deliberately different at the outline level — ring,
 * triangle, disc, ring — and different again at the mark level, so neither
 * colour nor fine detail is doing the work on its own.
 */
export const TONE_GLYPH: Record<Tone, string> = {
  // A ring with a solid centre: a signal that is on air.
  live:
    "M8 2a6 6 0 1 1 0 12A6 6 0 0 1 8 2Zm0 1.6a4.4 4.4 0 1 0 0 8.8 4.4 4.4 0 0 0 0-8.8Z" +
    "M8 5.4a2.6 2.6 0 1 1 0 5.2 2.6 2.6 0 0 1 0-5.2Z",
  // A triangle with an exclamation punched out.
  attention:
    "M8 1.4 15.2 14.2H0.8L8 1.4Zm0 3.9a.95.95 0 0 0-.95 1.04l.31 3.3a.64.64 0 0 0 1.28 0l.31-3.3A.95.95 0 0 0 8 5.3Z" +
    "M8 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
  // A solid disc with a cross punched out.
  failed:
    "M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm2.9 8.35-1.05 1.05L8 9.05l-1.85 1.85L5.1 9.85 6.95 8 5.1 6.15 " +
    "6.15 5.1 8 6.95 9.85 5.1l1.05 1.05L9.05 8l1.85 1.85Z",
  // A ring with a single bar: nothing has happened yet.
  neutral:
    "M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm0 1.5a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z" +
    "M5.25 7.25h5.5v1.5h-5.5Z",
};

/**
 * A tone the server sent as a string (a state's `tone` column, `0072`). Anything
 * that is not one of the four reads as neutral rather than as a guessed colour.
 */
export function toneOf(value: string): Tone {
  return (TONES as readonly string[]).includes(value) ? (value as Tone) : "neutral";
}

/** The CSS custom-property family each tone reads. Never a raw colour token. */
export function tokenPrefixFor(tone: Tone): string {
  return `--status-${tone}`;
}

/** The class the chip renders with. status.css owns the other end of this. */
export function classNameFor(tone: Tone): string {
  return `chip chip-${tone}`;
}

export type StatusChipModel = {
  tone: Tone;
  /** The words. Always present, never derived from the tone. */
  label: string;
  className: string;
  /** SVG path data. Decorative: the label is the accessible name. */
  glyph: string;
};

/**
 * The ONLY way a status is constructed.
 *
 * The icon is not a parameter. A caller cannot omit it, cannot pick a
 * mismatched one, and cannot invent a fifth. That is the point: an affordance
 * that can be got wrong eventually is.
 */
export function statusChip(tone: Tone, label: string, translate: (text: string) => string = (x) => x): StatusChipModel {
  // UX pass (21 October 2026): the chip puts its words through the dictionary itself, so a server word
  // ("Approved") reaches the reader in their language on every screen. Translating already-translated text
  // is a no-op: it has no key.
  const words = translate(label.trim()).trim();
  if (words === "") {
    // Deliberately loud, and deliberately at construction time. A chip with no
    // words is a status conveyed by colour alone — the exact defect the design
    // system exists to prevent — and rendering an empty one would hide it
    // behind something that still looks like a working interface.
    throw new Error("a status must carry a text label, never colour alone");
  }
  return { tone, label: words, className: classNameFor(tone), glyph: TONE_GLYPH[tone] };
}

/**
 * True when no two tones share a silhouette.
 *
 * Asserted by the test suite rather than trusted, because the failure mode is
 * invisible to anyone with normal colour vision: two tones with the same glyph
 * look completely distinct on the reviewer's screen and identical to the person
 * the rule was written for.
 */
export function glyphsAreDistinct(glyphs: Record<Tone, string> = TONE_GLYPH): boolean {
  const seen = new Set<string>();
  for (const tone of TONES) {
    const path = glyphs[tone];
    if (!path || path.trim() === "") return false;
    if (seen.has(path)) return false;
    seen.add(path);
  }
  return true;
}
